const CLIENT_COMPANY_STORAGE_KEY = "worktime.client.company.id";
const KIOSK_EMPLOYEE_STORAGE_KEY = "worktime.kiosk.employee.id";
const DEVICE_UUID_STORAGE_KEY = "worktime-device-uuid";

let runtimeAccessToken: string | null = null;

export interface AuthContextSnapshot {
  accessToken: string | null;
  clientCompanyId: string | null;
  kioskEmployeeId: string | null;
  deviceUuid: string | null;
}

function canUseWindow(): boolean {
  return typeof window !== "undefined";
}

function readStorage(key: string): string | null {
  if (!canUseWindow()) {
    return null;
  }

  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    console.warn(`Unable to read localStorage key "${key}"`, error);
    return null;
  }
}

function writeStorage(key: string, value: string | null): void {
  if (!canUseWindow()) {
    return;
  }

  try {
    if (value == null || value.trim().length === 0) {
      window.localStorage.removeItem(key);
      return;
    }

    window.localStorage.setItem(key, value);
  } catch (error) {
    console.warn(`Unable to write localStorage key "${key}"`, error);
  }
}

function normalizeToken(token?: string | null): string | null {
  if (typeof token !== "string") {
    return null;
  }

  const trimmedToken = token.trim();
  return trimmedToken.length > 0 ? trimmedToken : null;
}

export function getRuntimeAccessToken(): string | null {
  return runtimeAccessToken;
}

export function getAccessToken(): string | null {
  const globalToken = canUseWindow()
    ? normalizeToken(window.keycloak?.token ?? window.kc_token ?? null)
    : null;

  if (globalToken) {
    runtimeAccessToken = globalToken;
    return runtimeAccessToken;
  }

  return runtimeAccessToken;
}

export function setAccessToken(token: string | null): void {
  runtimeAccessToken = normalizeToken(token);

  if (!canUseWindow()) {
    return;
  }

  if (runtimeAccessToken) {
    window.kc_token = runtimeAccessToken;
  } else {
    delete window.kc_token;
  }
}

export function clearAccessToken(): void {
  setAccessToken(null);
}

export function getClientCompanyId(): string | null {
  return readStorage(CLIENT_COMPANY_STORAGE_KEY);
}

export function setClientCompanyId(companyId: string | number | null): void {
  writeStorage(
    CLIENT_COMPANY_STORAGE_KEY,
    companyId == null ? null : String(companyId),
  );
}

export function clearClientCompanyId(): void {
  writeStorage(CLIENT_COMPANY_STORAGE_KEY, null);
}

export function getKioskEmployeeId(): string | null {
  return readStorage(KIOSK_EMPLOYEE_STORAGE_KEY);
}

export function setKioskEmployeeId(employeeId: string | number | null): void {
  writeStorage(
    KIOSK_EMPLOYEE_STORAGE_KEY,
    employeeId == null ? null : String(employeeId),
  );
}

export function clearKioskEmployeeId(): void {
  writeStorage(KIOSK_EMPLOYEE_STORAGE_KEY, null);
}

export function getDeviceUuid(): string | null {
  return readStorage(DEVICE_UUID_STORAGE_KEY);
}

export function getAuthContextSnapshot(): AuthContextSnapshot {
  return {
    accessToken: getAccessToken(),
    clientCompanyId: getClientCompanyId(),
    kioskEmployeeId: getKioskEmployeeId(),
    deviceUuid: getDeviceUuid(),
  };
}

export function clearAuthContext(options?: {
  preserveClientCompany?: boolean;
  preserveDeviceUuid?: boolean;
  preserveKioskEmployee?: boolean;
}): void {
  clearAccessToken();

  if (options?.preserveClientCompany !== true) {
    clearClientCompanyId();
  }

  if (options?.preserveKioskEmployee !== true) {
    clearKioskEmployeeId();
  }

  if (options?.preserveDeviceUuid !== true) {
    writeStorage(DEVICE_UUID_STORAGE_KEY, null);
  }
}

export {
  CLIENT_COMPANY_STORAGE_KEY,
  DEVICE_UUID_STORAGE_KEY,
  KIOSK_EMPLOYEE_STORAGE_KEY,
};
